import React from 'react';
import {
  Box,
  SimpleGrid,
  VStack,
  HStack,
  Text,
  Badge,
  Progress,
  useColorModeValue,
  Icon,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  StatArrow,
  Divider,
  CircularProgress,
  CircularProgressLabel,
} from '@chakra-ui/react';
import { Brain, Activity, GitBranch, Zap, Database, MessageSquare, Users } from 'lucide-react';

const DashboardPanel = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const methodistBlue = '#0066CC';

  const systemMetrics = [
    { 
      title: "System Health", 
      value: "98.2%", 
      change: 1.2, 
      icon: Activity, 
      color: "#38A169" // Green
    },
    { 
      title: "Active Models", 
      value: "14", 
      change: 2, 
      icon: Brain, 
      color: methodistBlue 
    },
    { 
      title: "Data Throughput", 
      value: "2.4K/s", 
      change: 15, 
      icon: Zap, 
      color: "#805AD5" // Purple
    },
    { 
      title: "Knowledge Nodes", 
      value: "1.2M", 
      change: 8.4, 
      icon: Database, 
      color: "#DD6B20" // Orange
    }
  ];

  const cortexStatus = [
    {
      name: "Admin Cortex",
      metrics: {
        activeUsers: 42,
        processedIdeas: 124,
        avgResponseTime: "45ms"
      },
      health: 98
    },
    {
      name: "Clinical Cortex",
      metrics: {
        activeUsers: 876,
        contentDelivery: "2.1K/hr",
        satisfaction: "94.2%"
      },
      health: 96
    }
  ];

  const integrationMetrics = [
    {
      title: "Cross-Cortex Sync",
      value: "99.2%",
      description: "Data synchronization between Admin and Clinical cortices",
      trend: "+1.2%"
    },
    {
      title: "Content Adaptation",
      value: "95.8%",
      description: "AI-driven content personalization accuracy",
      trend: "+2.4%"
    },
    {
      title: "Governance Compliance",
      value: "100%",
      description: "Policy and regulatory adherence score",
      trend: "0%"
    }
  ];

  const recentActivity = [
    {
      type: "model",
      event: "Clinical Context Engine v2.3 deployed",
      time: "10 minutes ago",
      impact: "High"
    },
    {
      type: "sync",
      event: "Knowledge Graph sync completed",
      time: "25 minutes ago",
      impact: "Medium"
    },
    {
      type: "alert",
      event: "Content delivery latency optimization",
      time: "1 hour ago",
      impact: "Low"
    }
  ];

  return (
    <VStack spacing={6} align="stretch">
      {/* System Metrics */}
      <SimpleGrid columns={4} spacing={4}>
        {systemMetrics.map((metric, index) => (
          <Box 
            key={index} 
            bg={bgColor} 
            p={4} 
            borderRadius="md" 
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <HStack mb={2}>
              <Icon as={metric.icon} color={metric.color} boxSize={5} />
              <Text fontSize="sm" fontWeight="medium">{metric.title}</Text>
            </HStack>
            <Stat>
              <StatNumber fontSize="2xl" color={metric.color}>{metric.value}</StatNumber>
              <StatHelpText mb={0}>
                <StatArrow type={metric.change > 0 ? 'increase' : 'decrease'} />
                {Math.abs(metric.change)}%
              </StatHelpText>
            </Stat>
          </Box>
        ))}
      </SimpleGrid>

      {/* Cortex Status */}
      <SimpleGrid columns={2} spacing={4}>
        {cortexStatus.map((cortex, index) => (
          <Box
            key={index}
            bg={bgColor}
            p={4}
            borderRadius="md"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <HStack justify="space-between" mb={4}>
              <HStack>
                <Icon as={index === 0 ? Brain : Users} color="methodist.blue" boxSize={5} />
                <Text fontSize="lg" fontWeight="medium">{cortex.name}</Text>
              </HStack>
              <CircularProgress value={cortex.health} color="green.400" size="40px">
                <CircularProgressLabel fontSize="xs">{cortex.health}%</CircularProgressLabel>
              </CircularProgress>
            </HStack>
            <SimpleGrid columns={3} spacing={4}>
              {Object.entries(cortex.metrics).map(([key, value]) => (
                <Box key={key}>
                  <Text fontSize="sm" color="gray.500" textTransform="capitalize">
                    {key.replace(/([A-Z])/g, ' $1').trim()}
                  </Text>
                  <Text fontSize="sm" fontWeight="medium">{value}</Text>
                </Box>
              ))}
            </SimpleGrid>
          </Box>
        ))}
      </SimpleGrid>

      {/* Integration Metrics */}
      <SimpleGrid columns={3} spacing={4}>
        {integrationMetrics.map((metric, index) => (
          <Box
            key={index}
            bg={bgColor}
            p={4}
            borderRadius="md"
            boxShadow="sm"
            borderWidth="1px"
            borderColor={borderColor}
          >
            <VStack align="start" spacing={2}>
              <Text fontSize="sm" fontWeight="medium">{metric.title}</Text>
              <HStack justify="space-between" width="100%">
                <Text fontSize="2xl" fontWeight="bold" color="methodist.blue">
                  {metric.value}
                </Text>
                <Badge colorScheme={
                  metric.trend.startsWith('+') ? 'green' : 
                  metric.trend.startsWith('-') ? 'red' : 
                  'gray'
                }>
                  {metric.trend}
                </Badge>
              </HStack>
              <Text fontSize="sm" color="gray.500">{metric.description}</Text>
            </VStack>
          </Box>
        ))}
      </SimpleGrid>

      {/* Recent Activity */}
      <Box
        bg={bgColor}
        p={4}
        borderRadius="md"
        boxShadow="sm"
        borderWidth="1px"
        borderColor={borderColor}
      >
        <Text fontSize="lg" fontWeight="medium" mb={3}>Recent Activity</Text>
        <VStack spacing={3} align="stretch">
          {recentActivity.map((activity, index) => (
            <React.Fragment key={index}>
              <HStack justify="space-between">
                <HStack spacing={3}>
                  <Icon 
                    as={
                      activity.type === 'model' ? Brain :
                      activity.type === 'sync' ? Activity :
                      MessageSquare
                    }
                    color={
                      activity.type === 'model' ? 'blue.500' :
                      activity.type === 'sync' ? 'green.500' :
                      'orange.500'
                    }
                    boxSize={4}
                  />
                  <Text fontSize="sm">{activity.event}</Text>
                </HStack>
                <HStack spacing={3}>
                  <Badge 
                    colorScheme={
                      activity.impact === 'High' ? 'red' :
                      activity.impact === 'Medium' ? 'orange' :
                      'green'
                    }
                  >
                    {activity.impact}
                  </Badge>
                  <Text fontSize="sm" color="gray.500">{activity.time}</Text>
                </HStack>
              </HStack>
              {index < recentActivity.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </VStack>
      </Box>
    </VStack>
  );
};

export default DashboardPanel;
